import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import Modal from '../common/Modal';
import NoteEditor, { type NoteEditorHandle } from './NoteEditor';

interface NotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  notes: string;
  onSave: (notes: string) => void | Promise<unknown>;
  title: string;
  placeholder?: string;
  maxLength?: number;
}

const NotesModal: React.FC<NotesModalProps> = ({
  isOpen,
  onClose,
  notes,
  onSave,
  title,
  placeholder,
  maxLength,
}) => {
  const { formatMessage: t } = useIntl();
  const editorRef = useRef<NoteEditorHandle>(null);
  const initialRef = useRef(notes);

  useEffect(() => {
    if (!isOpen) return;
    initialRef.current = notes;
    editorRef.current?.reset(notes);
  }, [notes, isOpen]);

  const handleSave = async () => {
    try {
      await onSave(editorRef.current?.getHtml() ?? '');
      onClose();
    } catch {
      // Parent shows the error toast and keeps this modal open.
    }
  };

  const handleCancel = () => {
    editorRef.current?.reset(initialRef.current);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      onClose={handleCancel}
      title={title}
      maxWidth={640}
      onSave={handleSave}
      saveLabel={t({ id: 'common.save' })}
    >
      <NoteEditor
        ref={editorRef}
        notes={notes}
        placeholder={placeholder}
        maxLength={maxLength}
      />
    </Modal>
  );
};

export default NotesModal;
